<template>
  <div class="residential-properties-input">
    <slot name="before" />
    <EditableBoard
      :columns="[
        { id: 'key', label: $t('fieldType'), size: '1fr' },
        { id: 'value', label: $t('customizeField'), size: '1fr', align: 'end' },
        { id: 'dropdown', size: '1fr', align: 'end' },
      ]"
      :sortable="sortable"
      :value="filteredItems"
      @update-order="updateOrder"
      :custom-add="() => (create = true)"
      :create-label="createLabel"
    >
      <template #key="{ item }">
        <InputComponent
          :value="item.label || $t(item.key)"
          @input="$ev => updateCustomItem(item, $ev)"
          :disabled="isDeleted(item) || disabled || Boolean(getDisabledField('properties.' + item.key))"
          :readonly="disabled || PropertiesEnum.find(p => p.key === item.key)"
          placeholder="..."
        />
      </template>
      <template #value="{ item }">
        <div class="residential-properties-input__value">
          <component
            v-if="item.type !== 'facility' && !isDeleted(item)"
            :is="getComponent(item)"
            :value="item.value"
            @input="$ev => itemUpdate(item, $ev)"
            align="right"
            v-bind="getValueBindings(item)"
            :disabled="disabled || Boolean(getDisabledField('properties.' + item.key))"
            v-tooltip.bottom="getDisabledField('properties.' + item.key)"
            :placeholder="getPlaceholder(item)"
          />
        </div>
      </template>
      <template v-if="!disabled" #dropdown="{ item }">
        <DropdownComponent v-if="!isRequired(item)" class="residential-properties-input__remove" :close-on-click="true">
          <template #default>
            <ButtonComponent :icon="'fa-pro-light:angle-down'" />
          </template>
          <template #dropdown="{ close, visible }">
            <ButtonComponent
              v-if="!Boolean(getDisabledField('properties.' + item.key))"
              :icon="['fal', 'times']"
              :label="$t('delete')"
              type="button"
              @click="remove(item, true)"
            />
            <ButtonComponent
              v-if="type === 'facility'"
              :icon="['fal', 'times']"
              :label="$t('changeIcon')"
              type="button"
              @click="editIcon(item)"
            />
          </template>
        </DropdownComponent>
      </template>
    </EditableBoard>

    <CreateFields
      :type="type"
      :show.sync="create"
      @add="$ev => add($ev, true)"
      :items="filteredProperties"
      :custom="customItems"
      :used="used"
      :get-disabled-field="getDisabledField"
    ></CreateFields>

    <IconSelector
      v-if="current"
      :value="current"
      :type="type"
      :show.sync="showIconSelector"
      @update="$ev => addIcon($ev)"
    ></IconSelector>
    <slot name="after" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { Query } from 'mingo'
import { CamelCase, Slugify, Debounce } from '@/utils'

import { InputComponent } from 'vue-elder-input'
import { DropdownComponent } from 'vue-elder-dropdown'
import Types from './Types'
import CreateFields from './CreateFields'
import IconSelector from './IconSelector'

import EditableBoard from '@/components/EditableBoard.vue'

function sortByAnotherList(items, order, key) {
  // Create a map of order indices based on the order list
  const orderMap = new Map(order.map((item, index) => [item[key], index]))

  // Sort the items based on the index from the order map
  return items.sort((a, b) => {
    const indexA = orderMap.has(a[key]) ? orderMap.get(a[key]) : Number.MAX_SAFE_INTEGER
    const indexB = orderMap.has(b[key]) ? orderMap.get(b[key]) : Number.MAX_SAFE_INTEGER
    return indexA - indexB
  })
}

export default {
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    featured: {
      type: Array,
      default: () => [],
    },
    filter: {
      type: Function,
      default: p => p,
    },
    used: {
      type: Array,
      default: () => [],
    },
    sortable: {
      type: Boolean,
      default: true,
    },
    requiredCheck: {
      type: Boolean,
      default: true,
    },
    showDeletedItems: {
      type: Boolean,
      default: false,
    },
    createLabel: {
      type: String,
    },

    type: {
      default: 'property',
      enum: ['facility', 'property'],
    },
    parent: {
      type: Object,
      default: () => ({}),
    },
    model: {
      type: String,
      defualt: 'Residential',
      enum: ['Residential', 'ResidentialTemplate'],
    },
    currency: String,
    disabled: Boolean,
    disabledFields: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      create: false,
      showIconSelector: false,
      current: null,
      itemsAddedFromUser: [],
      customEditedItems: [],
    }
  },
  computed: {
    queryObject() {
      return {
        ...this.parent,
        properties: this.value,
        model: this.model,
        sale: {
          type: this.$path('sale.type', this.parent) || 'sale',
        },
      }
    },
    ...mapState({
      PropertiesEnum(state) {
        return state.enums.ResidentialProperties || []
      },
    }),
    filteredProperties() {
      return (
        this.PropertiesEnum.filter(e => {
          if (!e.condition || !this.requiredCheck) return true
          if (this.value.find(i => i.key === e.key)) return true
          if (new Query(e.condition).test(this.queryObject)) return true

          return false
        })
          .map(p => {
            if (p.unit !== 'Enum') return p
            return {
              ...p,
              values: p.values
                .filter(e => {
                  if (!e.condition) return true

                  if (new Query(e.condition).test(this.queryObject)) return true

                  let match = this.value.find(v => v.key === p.key && e.value === v.value)
                  if (!match) return false

                  match.value = null

                  return false
                })
                .map(e => (typeof e === 'string' ? e : e.value)),
            }
          })
          //add facility as type if not saved in DB
          .map(p => ({
            ...p,
            inUse: this.value.find(i => i.key === p.key && !this.isDeleted(i)),
            type: this.PropertiesEnum.find(i => i.key === p.key && i.type === 'facility') ? 'facility' : '',
          }))
          .filter(i => this.filter(i))
      )
    },
    customItems() {
      const usedProperties = this.$path('project.metadata.siteSettings.allUsedResidentialProperties', this.parent) || {}

      return [
        ...this.value,
        ...Object.entries(usedProperties)
          .map(([key, value]) => ({
            ...value,
            key: key,
          }))
          .filter(p => !this.value.find(i => i.key === p.key)),
      ]
        .filter(v => !this.PropertiesEnum.find(p => p.key === v.key))
        .map(p => ({ ...p, inUse: Boolean(this.value.find(i => i.key === p.key && !this.isDeleted(i))) }))
        .filter(p => this.filter(p)) // Custom properties
    },
    filteredItems() {
      return this.value
        .filter(p => this.filter(p))
        .filter(i =>
          this.showDeletedItems ? (this.customEditedItems.includes(i.key) ? false : true) : i.value !== 'null',
        )
    },
    items() {
      return sortByAnotherList(
        [
          ...this.filteredProperties.map(e => (this.value || []).find(v => v.key === e.key)).filter(Boolean), // Standard properties
        ],
        this.value,
        'key',
      )
    },

    unusedProperties() {
      if (!this.value) return []
      return this.filteredProperties.filter(p => !this.value.find(v => v.key === p.key) || p.unit === 'Boolean')
    },
  },
  methods: {
    addIcon(icon) {
      if (!icon) return
      this.$emit(
        'input',
        this.value.map(v => ({ ...v, icon: v.key === this.current.key ? icon : v.icon })),
      )
    },
    editIcon(item) {
      this.current = item
      this.showIconSelector = true
    },
    updateOrder(items) {
      this.$emit('input', sortByAnotherList(this.value, items, 'key'))
    },
    generateKey(item) {
      let slug = CamelCase(Slugify(item.label))

      let MakeSafe = (iterator = 0) => {
        let newSlug = iterator ? slug + '-' + iterator : slug
        if (this.value.find(p => p.key === newSlug)) return MakeSafe(iterator + 1)
        return newSlug
      }
      return MakeSafe()
    },
    updateCustomItem: Debounce(function (item, value) {
      if (!value) return

      item.label = value

      const isSaved = !this.customEditedItems.includes(item.key)
      const newKey = this.generateKey(item)

      this.customEditedItems.push(item.key)

      //add new item on same position as old item
      if (isSaved) {
        const addOnIndex = this.value.findIndex(i => i.key === item.key)
        this.add(
          {
            ...item,
            key: newKey,
            isAdded: true,
          },
          false,
          addOnIndex,
        )
        item.value = 'null'
      } else {
        item.key = newKey
      }
    }, 300),

    add(value, addedFromUser, addOnIndex) {
      //if value is deleted and then added again -> update value to not be null
      if (addedFromUser) {
        this.itemsAddedFromUser.push(value.key)
      }
      const exists = null || this.value.find(({ key }) => key === value.key)

      if (exists && this.isDeleted(exists)) {
        let indexToRemove = this.value.findIndex(i => i.key === exists.key)

        if (indexToRemove > -1 && indexToRemove < this.value.length) {
          this.value.splice(indexToRemove, 1)
        } else return
      }

      const isBoolean = (value.value !== 'null' && this.type === 'facility') || value.unit === 'Boolean'
      let newKey = value.key
      if (!newKey || newKey.startsWith('custom')) newKey = this.generateKey(value)

      const data = {
        key: newKey,
        label: value.label,
        value: isBoolean ? true : value.value || null,
        type: value.type || this.type,
      }
      if (addOnIndex > -1) {
        //add item in a poition
        return this.value.splice(addOnIndex, 0, data)
      }

      this.value.push(data)
    },

    remove(value, removedFromUser) {
      let match = this.value.find(v => {
        if (typeof value === 'string') return v.key === value
        return v.key === value.key
      })
      if (!match) return
      if (removedFromUser) {
        this.itemsAddedFromUser = this.itemsAddedFromUser.filter(i => i !== match.key)
      }
      //the server will delete items with value 'null'
      this.$emit(
        'input',
        this.value.map(v => ({ ...v, value: v.key === match.key ? 'null' : v.value })),
      )
    },
    getEnumByKey(key) {
      return this.filteredProperties.find(p => p.key === key)
    },

    updateRequiredFields(includeSavedProperties) {
      if (!this.requiredCheck) return

      //exlude added user items and items that is saved
      const itemsToUpdate = this.value

      const currentRequiredItems = [
        ...this.PropertiesEnum.filter(i => this.isRequired(i)),
        ...(includeSavedProperties ? this.$path('properties', this.parent) : []),
        ...this.value.filter(i => this.itemsAddedFromUser.includes(i.key)),
      ].filter((item, index, self) => index === self.findIndex(obj => obj.key === item.key))

      const itemsToAdd = currentRequiredItems.filter(i => {
        if (itemsToUpdate.find(p => p.key === i.key && !this.isDeleted(p))) return false
        return true
      })

      const itemsToDelete = this.value.filter(
        i =>
          !currentRequiredItems.find(p => p.key === i.key) &&
          (typeof i.value === 'boolean' ? false : !i.value && !this.isDeleted(i)),
      )

      // remove empty not required items
      itemsToDelete.forEach(item => {
        setTimeout(() => this.remove(item), 100)
      })

      //add missing required items
      itemsToAdd.forEach(item => {
        setTimeout(() => this.add(item), 100)
      })
    },

    itemUpdate(item, value) {
      let result = this.value

      let index = result.findIndex(i => i.key === item.key)
      if (!~index) return
      result.splice(index, 1, {
        ...item,
        value: value,
      })

      const savedProperty = ((this.$path('properties', this.parent) || []).find(i => i.key === 'propertyType') || {})
        .value
      this.$emit('input', result)

      if (item.key === 'propertyType' && this.type !== 'facility') {
        this.updateRequiredFields(savedProperty)
      }
    },
    getPlaceholder(item) {
      switch (this.getComponent(item)) {
        case Types.Select:
        case Types.Boolean:
          return `${this.$t('select')}...`
        default:
          return `${this.$t('typeHere')}...`
      }
    },
    isDeleted(value) {
      return value.value === 'null'
    },
    isRequired(value) {
      if ((this.used.find(({ key }) => key === value.key) || {}).preventDelete) return true
      if (this.isDeleted(value)) return true
      if (!this.requiredCheck) return false
      let match = this.getEnumByKey(value.key)

      if (!match || !match.required) return false

      if (typeof match.required === 'boolean') return match.required

      if (new Query(match.required).test(this.queryObject)) return true
      return false
    },
    getComponent(value) {
      let match = this.getEnumByKey(value.key)
      if (!match) return 'InputComponent'

      switch (match.unit) {
        case 'Enum':
          return Types.Select
        case 'Boolean':
          return Types.Boolean
        default:
          return 'InputComponent'
      }
    },

    getValueBindings(value) {
      let binding = {
        suffix: ' ',
      }
      let match = this.getEnumByKey(value.key)
      if (!match) return binding

      // Set Suffix
      switch (match.unit) {
        case 'SquareMetre':
          binding.suffix = 'm²'
          break
        case 'Metre':
          binding.suffix = 'm'
          break
        case 'Currency':
          binding.suffix = this.currency
          break
      }

      // Set Type
      switch (match.unit) {
        case 'Int':
          binding.mask = { mask: Number, unmask: true, scale: 0 }
          break
        case 'SquareMetre':
        case 'Metre':
        case 'Float':
          binding.mask = { mask: Number, unmask: true, scale: 2 }
          break
        case 'Currency':
          binding.mask = { mask: Number, thousandsSeparator: ' ', unmask: true, scale: 2 }
          break
      }

      if (match.unit === 'Enum')
        binding.items = match.values.map(v => ({ label: match.i18n ? this.$tc(`${value.key}:${v}`) : v, value: v }))

      return binding
    },
    getDisabledField(key) {
      if (Object.keys(this.disabledFields).length) return this.disabledFields[key]
      let getter = this.$store.getters[`${this.model}/getDisabledField`]
      if (!getter) return
      return getter(key)
    },
  },
  components: {
    InputComponent,
    DropdownComponent,
    EditableBoard,
    CreateFields,
    IconSelector,
  },
}
</script>
<style lang="scss">
.residential-properties-input {
  $padding: 1.1rem;

  .elder-input:not(.field-tags) {
    .elder-input__field,
    .elder-input__suffix {
      background-color: transparent;
      border-width: 0;

      &--disabled:before {
        background-color: transparent;
      }
    }
  }

  .elder-input {
    &__suffix {
      color: currentColor;
      padding-left: 0;
      width: 3rem;
    }
    &__field--disabled {
      color: var(--grey);
    }
  }

  &__remove {
    padding-right: $padding;
    .elder-button {
      display: flex;
    }
    .elder-button__icon {
      padding: 0.5rem 0.75rem !important;
      display: flex;
    }
  }

  .editable-board {
    &__cell {
      padding: 0;
    }
    &__header {
      padding: 0.5rem $padding;
    }
  }
}
</style>
